/**
 * Taxonomy API Service
 * Service for fetching categories, specialties, and expertise reference data
 */

import { apiClient } from '@/core/api/client';

export type TaxonomyOption = {
  id: string;
  name: string;
  description?: string | null;
  displayOrder?: number;
  categoryId?: string | null; // For specialties: parent category ID
};

export type CategoriesResponse = {
  categories: TaxonomyOption[];
  total: number;
};

export type SpecialtiesResponse = {
  specialties: TaxonomyOption[];
  total: number;
};

export type ExpertiseResponse = {
  expertise: TaxonomyOption[];
  total: number;
};

export type TaxonomyResponseKey = 'categories' | 'specialties' | 'expertise';

export const normalizeTaxonomyOptions = (
  data: unknown,
  responseKey: TaxonomyResponseKey,
): TaxonomyOption[] => {
  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === 'object') {
    const responseValue = (data as Record<string, unknown>)[responseKey];
    return Array.isArray(responseValue) ? responseValue : [];
  }

  return [];
};

/**
 * Taxonomy API client
 */
export const taxonomyApi = {
  /**
   * Get all active categories
   */
  async getCategories(): Promise<TaxonomyOption[]> {
    const response = await apiClient.get<CategoriesResponse>('/taxonomy/categories');
    return normalizeTaxonomyOptions(response.data, 'categories');
  },

  /**
   * Get all active specialties
   * @param categoryId - Optional category ID to filter specialties
   */
  async getSpecialties(categoryId?: string): Promise<TaxonomyOption[]> {
    const params = categoryId ? { categoryId } : {};
    const response = await apiClient.get<SpecialtiesResponse>('/taxonomy/specialties', { params });
    return normalizeTaxonomyOptions(response.data, 'specialties');
  },

  /**
   * Get all active expertise
   */
  async getExpertise(): Promise<TaxonomyOption[]> {
    const response = await apiClient.get<ExpertiseResponse>('/taxonomy/expertise');
    return normalizeTaxonomyOptions(response.data, 'expertise');
  },
};

